/**
 * QMS Compliance Service (Phase 1)
 * Requirement-level compliance calculation and gap report.
 * Extends the compliance concept from task execution to standard_requirements.
 */

import qmsStandardService from './qmsStandardService';
import qmsPolicyService from './qmsPolicyService';
import processService from './processService';

const COMPLIANT_STATUSES = ['Implemented', 'Verified'];

class QmsComplianceService {
  /**
   * Get requirement-level compliance for an organization.
   * Scopes to standards (processes) belonging to the org (isTemplate=false).
   * @param {string} organizationId
   * @param {object} options - { domain, policyDocumentId }
   * @returns {Promise<{ overall, criticalOnly, byStandard, byDomain, byPolicy }>}
   */
  async getRequirementLevelCompliance(organizationId, options = {}) {
    if (!organizationId) {
      throw new Error('organizationId is required');
    }
    try {
      let standards = await processService.getProcesses(organizationId);
      if (options.domain) {
        standards = standards.filter((s) => (s.domain || s.processCategory) === options.domain);
      }
      const standardIds = standards.map((s) => s.id);
      if (standardIds.length === 0) {
        return this._emptyComplianceResult();
      }

      const standardRequirements = await qmsStandardService.getStandardRequirementsForStandards(standardIds);
      if (standardRequirements.length === 0) {
        return this._emptyComplianceResult();
      }

      const requirementIds = [...new Set(standardRequirements.map((sr) => sr.requirementId))];
      const requirementsMap = await this._fetchRequirementsMap(requirementIds);
      const standardsMap = Object.fromEntries(standards.map((s) => [s.id, s]));

      const IN_PROGRESS_STATUSES = ['In Progress', 'At Risk'];

      const byStandard = {};
      const byDomain = {};
      const byPolicy = {};
      let total = 0;
      let compliant = 0;
      let inProgress = 0;
      let notStarted = 0;
      let criticalTotal = 0;
      let criticalCompliant = 0;

      for (const sr of standardRequirements) {
        const req = requirementsMap[sr.requirementId];
        const standard = standardsMap[sr.standardId];
        if (!standard) continue;

        const status = sr.implementationStatus || 'Not Started';
        const isCompliant = COMPLIANT_STATUSES.includes(status);
        const isInProgress = IN_PROGRESS_STATUSES.includes(status);
        const isCritical = req?.complianceLevel === 'MUST';

        total++;
        if (isCompliant) compliant++;
        else if (isInProgress) inProgress++;
        else notStarted++;

        if (isCritical) {
          criticalTotal++;
          if (isCompliant) criticalCompliant++;
        }

        const rawDomain = standard.domain || standard.processCategory;
        const domain = typeof rawDomain === 'string' ? rawDomain || 'Other' : 'Other';
        const policyDocId = req?.policyDocumentId || 'unknown';

        if (!byStandard[sr.standardId]) byStandard[sr.standardId] = { total: 0, compliant: 0, inProgress: 0 };
        byStandard[sr.standardId].total++;
        if (isCompliant) byStandard[sr.standardId].compliant++;
        else if (isInProgress) byStandard[sr.standardId].inProgress++;

        if (!byDomain[domain]) byDomain[domain] = { total: 0, compliant: 0, inProgress: 0 };
        byDomain[domain].total++;
        if (isCompliant) byDomain[domain].compliant++;
        else if (isInProgress) byDomain[domain].inProgress++;

        if (!byPolicy[policyDocId]) byPolicy[policyDocId] = { total: 0, compliant: 0, inProgress: 0 };
        byPolicy[policyDocId].total++;
        if (isCompliant) byPolicy[policyDocId].compliant++;
        else if (isInProgress) byPolicy[policyDocId].inProgress++;
      }

      const overall = total > 0 ? Math.round((compliant / total) * 100) : 0;
      const adoption = total > 0 ? Math.round(((compliant + inProgress) / total) * 100) : 0;
      const criticalOnly = criticalTotal > 0 ? Math.round((criticalCompliant / criticalTotal) * 100) : null;

      // Enrich byStandard with names and counts
      const byStandardEnriched = {};
      for (const [standardId, counts] of Object.entries(byStandard)) {
        const std = standardsMap[standardId];
        const rawStdDomain = std?.domain || std?.processCategory;
        byStandardEnriched[standardId] = {
          name: String(std?.processName || std?.name || standardId),
          code: String(std?.processCode || ''),
          domain: typeof rawStdDomain === 'string' ? rawStdDomain : '',
          percent: counts.total > 0 ? Math.round((counts.compliant / counts.total) * 100) : 0,
          adoptionPercent: counts.total > 0 ? Math.round(((counts.compliant + counts.inProgress) / counts.total) * 100) : 0,
          total: counts.total,
          compliant: counts.compliant,
          inProgress: counts.inProgress,
        };
      }

      // Enrich byPolicy — load policy document names
      const policyDocIds = [...new Set(Object.keys(byPolicy))].filter(id => id !== 'unknown');
      const policyDocsMap = await this._fetchPolicyDocumentsMap(policyDocIds);
      const byPolicyEnriched = {};
      for (const [policyDocId, counts] of Object.entries(byPolicy)) {
        const doc = policyDocsMap[policyDocId];
        byPolicyEnriched[policyDocId] = {
          title: doc?.title || doc?.code || policyDocId,
          code: doc?.code || '',
          percent: counts.total > 0 ? Math.round((counts.compliant / counts.total) * 100) : 0,
          adoptionPercent: counts.total > 0 ? Math.round(((counts.compliant + counts.inProgress) / counts.total) * 100) : 0,
          total: counts.total,
          compliant: counts.compliant,
          inProgress: counts.inProgress,
        };
      }

      // byDomain enriched
      const byDomainEnriched = {};
      for (const [domain, counts] of Object.entries(byDomain)) {
        byDomainEnriched[domain] = {
          name: domain,
          percent: counts.total > 0 ? Math.round((counts.compliant / counts.total) * 100) : 0,
          adoptionPercent: counts.total > 0 ? Math.round(((counts.compliant + counts.inProgress) / counts.total) * 100) : 0,
          total: counts.total,
          compliant: counts.compliant,
          inProgress: counts.inProgress,
        };
      }

      return {
        overall,
        adoption,
        criticalOnly,
        total,
        compliant,
        inProgress,
        notStarted,
        criticalTotal,
        criticalCompliant,
        byStandard: byStandardEnriched,
        byDomain: byDomainEnriched,
        byPolicy: byPolicyEnriched,
      };
    } catch (error) {
      console.error('[QmsComplianceService] getRequirementLevelCompliance:', error);
      throw error;
    }
  }

  /**
   * Get gap report: requirements not Implemented/Verified, prioritized.
   * @param {string} organizationId
   * @param {object} options - { domain, policyDocumentId, limit }
   * @returns {Promise<Array<{ standardRequirementId, requirementId, standardId, requirement, standard, policyDocument, status, complianceLevel, priority }>>}
   */
  async getGapReport(organizationId, options = {}) {
    if (!organizationId) {
      throw new Error('organizationId is required');
    }
    try {
      let standards = await processService.getProcesses(organizationId);
      if (options.domain) {
        standards = standards.filter((s) => (s.domain || s.processCategory) === options.domain);
      }
      const standardIds = standards.map((s) => s.id);
      if (standardIds.length === 0) return [];

      const standardRequirements = await qmsStandardService.getStandardRequirementsForStandards(standardIds);
      const gaps = standardRequirements.filter(
        (sr) => !COMPLIANT_STATUSES.includes(sr.implementationStatus || '')
      );

      if (gaps.length === 0) return [];

      const requirementIds = [...new Set(gaps.map((sr) => sr.requirementId))];
      const requirementsMap = await this._fetchRequirementsMap(requirementIds);
      const standardsMap = Object.fromEntries(standards.map((s) => [s.id, s]));

      const policyDocIds = [...new Set(Object.values(requirementsMap).map((r) => r?.policyDocumentId).filter(Boolean))];
      const policyDocsMap = await this._fetchPolicyDocumentsMap(policyDocIds);

      const report = gaps.map((sr) => {
        const req = requirementsMap[sr.requirementId];
        const standard = standardsMap[sr.standardId];
        const policyDoc = req?.policyDocumentId ? policyDocsMap[req.policyDocumentId] : null;
        const level = req?.complianceLevel || 'MAY';
        const priority = level === 'MUST' ? 3 : level === 'SHOULD' ? 2 : 1;
        return {
          standardRequirementId: sr.id,
          requirementId: sr.requirementId,
          standardId: sr.standardId,
          requirement: req,
          standard,
          policyDocument: policyDoc,
          status: sr.implementationStatus || 'Not Started',
          complianceLevel: level,
          priority,
        };
      });

      report.sort((a, b) => b.priority - a.priority || (a.requirement?.code || '').localeCompare(b.requirement?.code || ''));

      const limit = options.limit ?? 100;
      return report.slice(0, limit);
    } catch (error) {
      console.error('[QmsComplianceService] getGapReport:', error);
      throw error;
    }
  }

  _emptyComplianceResult() {
    return {
      overall: 0,
      adoption: 0,
      criticalOnly: null,
      total: 0,
      compliant: 0,
      inProgress: 0,
      notStarted: 0,
      criticalTotal: 0,
      criticalCompliant: 0,
      byStandard: {},
      byDomain: {},
      byPolicy: {},
    };
  }

  _toPercentMap(obj) {
    const result = {};
    for (const [key, val] of Object.entries(obj)) {
      result[key] = val.total > 0 ? Math.round((val.compliant / val.total) * 100) : 0;
    }
    return result;
  }

  async _fetchRequirementsMap(requirementIds) {
    const map = {};
    await Promise.all(
      requirementIds.map(async (id) => {
        try {
          const r = await qmsPolicyService.getRequirement(id);
          map[id] = r;
        } catch {
          map[id] = null;
        }
      })
    );
    return map;
  }

  async _fetchPolicyDocumentsMap(documentIds) {
    const map = {};
    await Promise.all(
      documentIds.map(async (id) => {
        try {
          const d = await qmsPolicyService.getPolicyDocument(id);
          map[id] = d;
        } catch {
          map[id] = null;
        }
      })
    );
    return map;
  }
}

export const qmsComplianceService = new QmsComplianceService();
export default qmsComplianceService;
